import { Copy as CopyIcon, Bookmark as BookmarkIcon, Loader2 as SpinnerIcon } from 'lucide-react';
import { Snackbar, Alert, Chip, IconButton, Tooltip } from '@mui/material';
import { useState, memo, useCallback, useMemo, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { prism, vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { useTheme } from '@mui/material/styles';
import type { Message } from '../../api/model';
import { formatChatMessageTime } from 'utils/dateUtils';
import AttachmentChipsList from '../AttachmentChipsList';
import MermaidDiagram from '../MermaidDiagram';
import styles from './MessageItem.module.scss';

interface MessageItemProps {
  message: Message;
  onSavePrompt: (content: string) => void;
  projectId?: string;
  chatId?: string | null;
}

/** Компактный формат счётчика токенов, как в Claude Code: 1234 → «1.2k». */
const formatTokens = (tokens: number): string => {
  if (tokens < 1000) return String(tokens);
  const thousands = tokens / 1000;
  const rounded = Math.round(thousands * 10) / 10;
  return `${rounded % 1 === 0 ? rounded.toFixed(0) : rounded.toFixed(1)}k`;
};

const MessageItem = memo(
  ({ message, onSavePrompt, projectId, chatId }: MessageItemProps) => {
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [copiedCode, setCopiedCode] = useState<string | null>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const theme = useTheme();
    const codeStyle = theme.palette.mode === 'dark' ? vscDarkPlus : prism;

    const handleCopyClick = useCallback(async (msg: Message) => {
      try {
        const contentEl = contentRef.current;
        if (contentEl) {
          const htmlContent = contentEl.innerHTML;
          const html = `<!DOCTYPE html><html><head><meta charset="utf-8"></head><body>${htmlContent}</body></html>`;
          const plain = contentEl.innerText || msg.content;
          await navigator.clipboard.write([
            new ClipboardItem({
              'text/html': new Blob([html], { type: 'text/html' }),
              'text/plain': new Blob([plain], { type: 'text/plain' }),
            }),
          ]);
        } else {
          await navigator.clipboard.writeText(msg.content);
        }
        setOpenSnackbar(true);
      } catch (err) {
        console.error('Не удалось скопировать текст: ', err);
      }
    }, []);

    const handleCopyCode = useCallback(async (code: string) => {
      try {
        await navigator.clipboard.writeText(code);
        setCopiedCode(code);
        setTimeout(() => setCopiedCode(null), 2000);
      } catch (err) {
        console.error('Не удалось скопировать код: ', err);
      }
    }, []);

    const handleCloseSnackbar = (_event?: React.SyntheticEvent | Event, reason?: string) => {
      if (reason === 'clickaway') return;
      setOpenSnackbar(false);
    };

    const codeComponent = useCallback(
      ({
        inline,
        className,
        children,
        ...props
      }: {
        inline?: boolean;
        className?: string;
        children?: React.ReactNode;
      }) => {
        const match = /language-(\w+)/.exec(className || '');
        const language = match ? match[1] : '';
        const codeString = String(children).replace(/\n$/, '');

        if (!inline && language === 'mermaid') {
          return <MermaidDiagram code={codeString} isStreaming={message.isStreaming} />;
        }

        return !inline && match ? (
          <div className={styles.codeBlockWrapper}>
            <div className={styles.codeBlockHeader}>
              <span>{language || 'code'}</span>
              <Tooltip title={copiedCode === codeString ? 'Скопировано!' : 'Скопировать код'}>
                <IconButton
                  size="small"
                  onClick={() => handleCopyCode(codeString)}
                  className={styles.codeBlockHeaderCopy}
                  aria-label="Скопировать код"
                >
                  <CopyIcon size={12} />
                </IconButton>
              </Tooltip>
            </div>
            <SyntaxHighlighter
              style={codeStyle}
              language={language}
              PreTag="div"
              className={styles.codeBlock}
              customStyle={{ background: 'transparent', padding: 0 }}
              {...props}
            >
              {codeString}
            </SyntaxHighlighter>
          </div>
        ) : (
          <code className={className} {...props}>
            {children}
          </code>
        );
      },
      [copiedCode, handleCopyCode, codeStyle, message.isStreaming]
    );

    const markdownComponents = useMemo(
      () => ({
        p: ({ children }: { children?: React.ReactNode }) => (
          <p className={styles.contentParagraph}>{children}</p>
        ),
        ul: ({ children }: { children?: React.ReactNode }) => (
          <ul className={styles.contentList}>{children}</ul>
        ),
        ol: ({ children }: { children?: React.ReactNode }) => (
          <ol className={styles.contentList}>{children}</ol>
        ),
        li: ({ children }: { children?: React.ReactNode }) => (
          <li className={styles.contentListItem}>{children}</li>
        ),
        table: ({ children }: { children?: React.ReactNode }) => (
          <div className={styles.tableWrapper}>
            <table>{children}</table>
          </div>
        ),
        code: codeComponent,
      }),
      [codeComponent]
    );

    const isUser = String(message.role).toLowerCase() === 'user';

    const showActions = message.created_at && (isUser || !message.isStreaming);

    const isEmptyAiStream = !isUser && message.isStreaming && !message.content;

    return (
      <div className={`${styles.row} ${isUser ? styles.user : ''}`}>
        {!isUser && (
          <div className={styles.aiBadge} aria-hidden>
            G
          </div>
        )}
        <div className={styles.messageBlock}>
          <div className={`${styles.bubble} ${isUser ? styles.user : styles.ai}`}>
            {!isUser && (message.progressLabel || message.progressTokens != null) && (
              <div className={styles.progressChip}>
                <Chip
                  icon={<SpinnerIcon size={12} />}
                  label={message.progressLabel ?? 'Генерация'}
                  size="small"
                  variant="outlined"
                />
                {message.progressTokens != null && (
                  <span className={styles.progressTokens}>
                    {formatTokens(message.progressTokens)} токенов
                  </span>
                )}
              </div>
            )}
            {isEmptyAiStream && !message.progressLabel && message.progressTokens == null && (
              <div className={styles.thinkingDots} aria-label="Думаю">
                <span />
                <span />
                <span />
              </div>
            )}
            {isUser ? (
              <div ref={contentRef} className={`${styles.content} ${styles.contentUser}`}>
                {message.content.split('\n').map((line, index, array) => (
                  <span key={index}>
                    {line}
                    {index < array.length - 1 && <br />}
                  </span>
                ))}
              </div>
            ) : (
              <div ref={contentRef} className={styles.content}>
                <ReactMarkdown
                  components={markdownComponents}
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeRaw, rehypeSanitize]}
                >
                  {message.content}
                </ReactMarkdown>
                {message.isStreaming && (
                  <span className={styles.streamingCursor} aria-hidden="true" />
                )}
              </div>
            )}

            {showActions && (
              <span className={`${styles.timestamp} ${isUser ? styles.user : styles.ai}`}>
                {formatChatMessageTime(message.created_at)}
              </span>
            )}
          </div>

          {isUser &&
            message.attached_document_ids &&
            message.attached_document_ids.length > 0 &&
            projectId && (
              <AttachmentChipsList
                documentIds={message.attached_document_ids}
                projectId={projectId}
                chatId={chatId ?? null}
              />
            )}

          {showActions && (
            <div className={`${styles.actionButtons} ${isUser ? styles.user : ''}`}>
              <Tooltip title="Скопировать">
                <IconButton
                  size="small"
                  onClick={() => handleCopyClick(message)}
                  className={styles.actionIcon}
                  aria-label="Скопировать"
                >
                  <CopyIcon size={16} />
                </IconButton>
              </Tooltip>
              {isUser && (
                <Tooltip title="Сохранить в промпты">
                  <IconButton
                    size="small"
                    onClick={() => onSavePrompt(message.content)}
                    className={styles.actionIcon}
                    aria-label="Сохранить"
                  >
                    <BookmarkIcon size={16} />
                  </IconButton>
                </Tooltip>
              )}
            </div>
          )}

          <Snackbar open={openSnackbar} autoHideDuration={3000} onClose={handleCloseSnackbar}>
            <Alert
              onClose={handleCloseSnackbar}
              severity="success"
              className={styles.snackbarAlert}
            >
              Текст скопирован в буфер обмена!
            </Alert>
          </Snackbar>
        </div>
      </div>
    );
  },
  (prevProps, nextProps) =>
    prevProps.message.content === nextProps.message.content &&
    prevProps.message.role === nextProps.message.role &&
    prevProps.message.created_at === nextProps.message.created_at &&
    prevProps.message.progressLabel === nextProps.message.progressLabel &&
    prevProps.message.progressTokens === nextProps.message.progressTokens &&
    prevProps.message.isStreaming === nextProps.message.isStreaming &&
    (prevProps.message.attached_document_ids?.join(',') ?? '') ===
      (nextProps.message.attached_document_ids?.join(',') ?? '') &&
    prevProps.projectId === nextProps.projectId &&
    prevProps.chatId === nextProps.chatId &&
    prevProps.onSavePrompt === nextProps.onSavePrompt
);

MessageItem.displayName = 'MessageItem';

export default MessageItem;
